(() => {
  const ROW_SELECTOR = '[data-mc-product-row]';
  const SCROLLER_SELECTOR = '[data-mc-product-row-scroller]';
  const CARD_SELECTOR = '.mc-product-card';
  const REDUCED_MOTION = window.matchMedia?.('(prefers-reduced-motion: reduce)');

  const getRows = (root) => {
    const rows = [];

    if (root instanceof Element && root.matches(ROW_SELECTOR)) {
      rows.push(root);
    }

    root.querySelectorAll?.(ROW_SELECTOR).forEach((row) => rows.push(row));
    return rows;
  };

  const getNearestCardIndex = (scroller, cards) => {
    const scrollerRect = scroller.getBoundingClientRect();
    const isRtl = getComputedStyle(scroller).direction === 'rtl';
    let nearestIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;

    cards.forEach((card, index) => {
      const cardRect = card.getBoundingClientRect();
      const distance = isRtl
        ? Math.abs(scrollerRect.right - cardRect.right)
        : Math.abs(cardRect.left - scrollerRect.left);

      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    return nearestIndex;
  };

  const initProductRow = (row) => {
    if (row.dataset.mcReady === 'true') return;

    const scroller = row.querySelector(SCROLLER_SELECTOR);
    if (!scroller) return;

    row.dataset.mcReady = 'true';

    const section = row.closest('.mc-featured-collection') || row;
    const previousButtons = [...section.querySelectorAll('[data-mc-product-row-prev]')];
    const nextButtons = [...section.querySelectorAll('[data-mc-product-row-next]')];
    let frameId = null;

    const updateSideArrowPosition = () => {
      const media = scroller.querySelector('.mc-product-card__media-wrap');
      if (!media) return;

      const rowRect = row.getBoundingClientRect();
      const mediaRect = media.getBoundingClientRect();
      const center = mediaRect.top - rowRect.top + mediaRect.height / 2;
      row.style.setProperty('--mc-side-arrow-top', `${Math.max(center, 0)}px`);
    };

    const updateState = () => {
      frameId = null;

      const cards = [...scroller.querySelectorAll(CARD_SELECTOR)];
      const scrollerRect = scroller.getBoundingClientRect();
      const tolerance = 2;
      const hasOverflow = scroller.scrollWidth - scroller.clientWidth > tolerance;
      const isRtl = getComputedStyle(scroller).direction === 'rtl';

      let atStart = true;
      let atEnd = true;

      if (cards.length > 0 && hasOverflow) {
        const firstRect = cards[0].getBoundingClientRect();
        const lastRect = cards[cards.length - 1].getBoundingClientRect();

        atStart = isRtl
          ? firstRect.right <= scrollerRect.right + tolerance
          : firstRect.left >= scrollerRect.left - tolerance;

        atEnd = isRtl
          ? lastRect.left >= scrollerRect.left - tolerance
          : lastRect.right <= scrollerRect.right + tolerance;
      }

      row.classList.toggle('is-scrollable', hasOverflow);
      row.classList.toggle('is-not-scrollable', !hasOverflow);
      section.classList.toggle('is-scrollable', hasOverflow);
      section.classList.toggle('is-not-scrollable', !hasOverflow);

      previousButtons.forEach((button) => {
        button.disabled = !hasOverflow || atStart;
        button.setAttribute('aria-disabled', String(button.disabled));
      });

      nextButtons.forEach((button) => {
        button.disabled = !hasOverflow || atEnd;
        button.setAttribute('aria-disabled', String(button.disabled));
      });

      updateSideArrowPosition();
    };

    const requestUpdate = () => {
      if (frameId !== null) return;
      frameId = window.requestAnimationFrame(updateState);
    };

    const moveByCard = (step) => {
      const cards = [...scroller.querySelectorAll(CARD_SELECTOR)];
      if (cards.length === 0) return;

      const currentIndex = getNearestCardIndex(scroller, cards);
      const targetIndex = Math.min(Math.max(currentIndex + step, 0), cards.length - 1);
      const target = cards[targetIndex];

      target.scrollIntoView({
        behavior: REDUCED_MOTION?.matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'start'
      });
    };

    previousButtons.forEach((button) => {
      button.addEventListener('click', () => moveByCard(-1));
    });

    nextButtons.forEach((button) => {
      button.addEventListener('click', () => moveByCard(1));
    });

    scroller.addEventListener('scroll', requestUpdate, { passive: true });

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(requestUpdate);
      resizeObserver.observe(row);
      resizeObserver.observe(scroller);
      row.mcResizeObserver = resizeObserver;
    } else {
      window.addEventListener('resize', requestUpdate, { passive: true });
    }

    updateState();
    section.classList.add('is-mc-navigation-ready');
  };

  const initWishlists = (root) => {
    root.querySelectorAll?.('.mc-product-card__wishlist.smartwishlist:not([data-mc-wl-ready])').forEach((wishlist) => {
      wishlist.dataset.mcWlReady = 'true';
      wishlist.addEventListener('click', () => {
        window.setTimeout(() => {
          const icon = wishlist.querySelector('i');
          if (!icon) return;

          if (wishlist.className.match(/(active|bookmarked|swym-added)/) || icon.classList.contains('heartfull')) {
            icon.classList.remove('heartoutline');
            icon.classList.add('heartfull');
            wishlist.classList.add('is-wishlisted');
          }
        }, 250);
      });
    });
  };

  const init = (root = document) => {
    getRows(root).forEach(initProductRow);
    initWishlists(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
